import {
	Culture,
	cultureLabels,
	UpdateVolunteersAboutRequest,
	VolunteersAboutFormData,
	VolunteersAboutRules,
} from "../context/VolunteersAboutContext/constants";
import { countText } from "../utils/textLength";

type TextField = "title" | "description";

const TEXT_FIELDS: Record<TextField, { label: string; maxLength: number; pattern: RegExp }> = {
	title: { label: "başlık", maxLength: VolunteersAboutRules.titleMaxLength, pattern: VolunteersAboutRules.textPattern },
	description: { label: "açıklama", maxLength: VolunteersAboutRules.descriptionMaxLength, pattern: VolunteersAboutRules.descriptionPattern },
};

const getCultureLabel = (culture: string) => cultureLabels[culture as Culture] ?? culture;

const validateText = (culture: string, field: TextField, value: string) => {
	const { label, maxLength, pattern } = TEXT_FIELDS[field];
	const trimmed = value.trim();
	// Sent as multipart form data: the backend counts every line break as \r\n.
	if (countText(trimmed, "crlf") <= maxLength && pattern.test(trimmed)) {
		return null;
	}
	return `${getCultureLabel(culture)} ${label} en fazla ${maxLength} karakter olmalı ve geçersiz karakter içermemelidir.`;
};

const validateImage = (culture: string, image: File) =>
	image.size <= VolunteersAboutRules.imageMaxFileSize && (VolunteersAboutRules.imageMimeTypes as readonly string[]).includes(image.type)
		? null
		: `${getCultureLabel(culture)} resim PNG, JPEG veya JPG formatında ve en fazla 20 MB olmalıdır.`;

// The add form creates every culture from scratch, so each field is required.
const validateNewLocalizable = (culture: string, title: string, description: string, image: File | null) => {
	const label = getCultureLabel(culture);
	if (!title.trim()) {
		return `Lütfen ${label} başlık alanını doldurun.`;
	}
	if (!description.trim()) {
		return `Lütfen ${label} açıklama alanını doldurun.`;
	}
	if (!image) {
		return `Lütfen ${label} resmi yükleyin.`;
	}
	return validateText(culture, "title", title) ?? validateText(culture, "description", description) ?? validateImage(culture, image);
};

// An update only carries what changed; an empty text there means the user cleared a filled field.
const validateChangedText = (culture: string, field: TextField, value?: string) => {
	if (value === undefined) {
		return null;
	}
	return value.trim() ? validateText(culture, field, value) : `${getCultureLabel(culture)} ${TEXT_FIELDS[field].label} boş bırakılamaz.`;
};

export class VolunteersAboutValidator {
	static readonly validateAddVolunteersAbout = (data: VolunteersAboutFormData): string | null => {
		for (const { culture, title, description, image } of data.volunteersAboutLocalizable) {
			const error = validateNewLocalizable(culture, title, description, image);
			if (error) {
				return error;
			}
		}
		return null;
	};

	static readonly validateUpdateVolunteersAbout = (data: UpdateVolunteersAboutRequest): string | null => {
		for (const { culture, title, description, image } of data.volunteersAboutLocalizable) {
			const error = validateChangedText(culture, "title", title)
				?? validateChangedText(culture, "description", description)
				?? (image ? validateImage(culture, image) : null);
			if (error) {
				return error;
			}
		}
		return null;
	};
}
